import { Button } from "@/components/ui/button";
import { useAutoGrowTextarea } from "@/hooks/use-auto-grow-textarea";
import { KEY_ENTER } from "@/lib/keyboard-keys";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import type { AskUserQuestion } from "../chat.utils";

interface Props {
  question: AskUserQuestion;
  isAnswered: boolean;
  answerContent?: string;
  onSubmit: (answer: string) => Promise<void>;
}

/**
 * CR-72: the option row and the indicator are deliberately squarer than the
 * rest of the chat surface. Under the widened radius scale, `rounded-sm`
 * turned the 14px checkbox into a blob indistinguishable from the
 * single-select dot; `rounded-xs` (2px) restores square-vs-circle at a glance.
 * One constant per shape so the answered and interactive render paths cannot
 * drift.
 */
const OPTION_ROW_CLASS = "flex items-start gap-2 rounded-xs px-1 py-0.5 text-sm";
const INDICATOR_BASE_CLASS =
  "relative mt-0.5 flex size-3.5 shrink-0 items-center justify-center border";

export function indicatorShapeClass(multiSelect: boolean): string {
  return multiSelect ? "rounded-xs" : "rounded-full";
}

/**
 * CR-200: reads the answer `handleSubmit` stores back into the selected labels
 * and their notes. A note typed with line breaks is stored as markdown lazy
 * continuation (`- **Label**: line1\nline2`), so every line after a bullet
 * belongs to that bullet's note until the next bullet. A continuation line
 * that itself starts with `- ` reads as a new bullet (the stored format has no
 * escaping and must stay as is). Lives beside `handleSubmit`, which defines the
 * format. Labels come from the AI's tool call, hence a Map (no inherited keys).
 */
export function parseAskUserAnswer(answerContent: string): {
  selectedLabels: Set<string>;
  notesByLabel: Map<string, string>;
} {
  const selectedLabels = new Set<string>();
  const notesByLabel = new Map<string, string>();
  let noteLabel: string | null = null;
  for (const line of answerContent.split(/\r?\n/)) {
    const withNote = /^- \*\*(.+?)\*\*(?:: (.+))?$/.exec(line);
    if (withNote) {
      const [, label, firstLine] = withNote;
      selectedLabels.add(label);
      noteLabel = firstLine ? label : null;
      if (firstLine) notesByLabel.set(label, firstLine);
      continue;
    }
    const plain = /^- (?!\*\*)(.+)$/.exec(line);
    if (plain) {
      selectedLabels.add(plain[1]);
      noteLabel = null;
      continue;
    }
    if (noteLabel !== null) notesByLabel.set(noteLabel, `${notesByLabel.get(noteLabel)}\n${line}`);
  }
  for (const [label, note] of notesByLabel) notesByLabel.set(label, note.trimEnd());
  return { selectedLabels, notesByLabel };
}

/** The option id the ask_user tool gives the free-text "Other" choice. */
const OTHER_OPTION_ID = "__other__";

/**
 * Builds the answer message stored for an ask_user prompt: the selected
 * options in option order, each with its trimmed note. `parseAskUserAnswer`
 * reads this format back, so it must not change. Takes the selection
 * explicitly (CR-234) so the Enter shortcut can submit a selection it has just
 * made, without waiting for React state to commit.
 */
export function buildAskUserAnswer(
  question: AskUserQuestion,
  selected: Record<string, boolean>,
  notes: Record<string, string>,
): string {
  const bullets = question.options
    .filter((opt) => selected[opt.id])
    .map((opt) => {
      const note = (notes[opt.id] ?? "").trim();
      return note ? `- **${opt.label}**: ${note}` : `- ${opt.label}`;
    })
    .join("\n");
  return `<!-- ask-user-answer -->\n**${question.question}**\n\n${bullets}`;
}

interface AskUserNoteFieldProps {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  /** Focus the field once on mount (CR-234: Enter on an unselected "Other"). */
  focusOnMount?: boolean;
}

/**
 * CR-200: the option note grows with its content like the chat composer
 * (CR-171 hook). Its own component so each selected option gets its own hook.
 */
function AskUserNoteField({ placeholder, value, onChange, focusOnMount }: AskUserNoteFieldProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useAutoGrowTextarea(ref, value);
  // Mount-only on purpose: the flag only matters when the field first appears.
  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, []);
  return (
    <textarea
      ref={ref}
      className="text-muted-foreground placeholder:text-muted-foreground/50 mt-0.5 ml-5 w-full resize-none overflow-hidden border-none bg-transparent text-xs outline-none"
      rows={1}
      placeholder={placeholder}
      maxLength={500}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function ChatMultiselectQuestion({ question, isAnswered, answerContent, onSubmit }: Props) {
  const options = question.options;

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Synchronous twin of isSubmitting: two Enter presses can land before the
  // state update re-renders, the ref still blocks the second one (CR-234).
  const submittingRef = useRef(false);
  const [focusNoteId, setFocusNoteId] = useState<string | null>(null);

  if (isAnswered) {
    const { selectedLabels, notesByLabel } = parseAskUserAnswer(answerContent ?? "");
    return (
      <div className="mt-2 flex flex-col gap-0.5">
        <p className="mb-1 text-sm font-medium">{question.question}</p>
        {options.map((opt) => {
          const isSelected = selectedLabels.has(opt.label);
          const note = notesByLabel.get(opt.label);
          return (
            <div key={opt.id} className={cn(OPTION_ROW_CLASS, !isSelected && "opacity-35")}>
              <span
                data-testid="ask-user-indicator"
                className={cn(
                  INDICATOR_BASE_CLASS,
                  indicatorShapeClass(question.multiSelect),
                  isSelected ? "border-primary bg-primary" : "border-muted-foreground/40",
                )}
              >
                {!!isSelected && <Check className="text-primary-foreground size-3" />}
              </span>
              <span>
                {opt.label}
                {!!note && (
                  <span className="text-muted-foreground ml-1 text-xs whitespace-pre-line">
                    — {note}
                  </span>
                )}
                {!isSelected && !!opt.description && (
                  <span className="text-muted-foreground ml-1 text-xs">— {opt.description}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  const hasSelection = Object.values(selected).some(Boolean);

  const toggleOption = (id: string) => {
    setFocusNoteId(null);
    setSelected((prev) => {
      if (!question.multiSelect) {
        // single-select: deselect everything else
        const wasSelected = prev[id];
        if (wasSelected) return {};
        setNotes({});
        return { [id]: true };
      }
      const next = { ...prev, [id]: !prev[id] };
      if (!next[id]) {
        setNotes((n) => {
          const { [id]: _, ...rest } = n;
          return rest;
        });
      }
      return next;
    });
  };

  const submitSelection = async (
    selection: Record<string, boolean>,
    selectionNotes: Record<string, string>,
  ) => {
    if (submittingRef.current || !Object.values(selection).some(Boolean)) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onSubmit(buildAskUserAnswer(question, selection, selectionNotes));
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit = () => submitSelection(selected, notes);

  /**
   * CR-234: Enter on a focused option button makes sure that option is
   * selected, then submits through the same path as the Submit button.
   * Single-select: the focused option replaces any other choice and its notes
   * (as a click would). Multi-select: the selected set plus the focused option.
   * Enter on an unselected "Other" selects it and moves focus to its note
   * instead of selecting and sending it in one stroke (Q2 default). Modified or
   * auto-repeated Enter keeps the native behaviour; note fields carry no
   * handler (Q1 default: newline).
   */
  const handleOptionKeyDown = (event: KeyboardEvent<HTMLButtonElement>, id: string) => {
    if (event.key !== KEY_ENTER) return;
    if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return;
    // Consume Enter: the native activation would fire a click that toggles the
    // option back off. A held key's repeats are swallowed, never re-submitted.
    event.preventDefault();
    if (event.repeat || submittingRef.current) return;
    const wasSelected = !!selected[id];
    const selection = question.multiSelect ? { ...selected, [id]: true } : { [id]: true };
    // Same rule as toggleOption: picking another single-select option drops notes.
    const selectionNotes = question.multiSelect || wasSelected ? notes : {};
    if (selectionNotes !== notes) setNotes(selectionNotes);
    setSelected(selection);
    if (id === OTHER_OPTION_ID && !wasSelected) {
      setFocusNoteId(id);
      return;
    }
    setFocusNoteId(null);
    void submitSelection(selection, selectionNotes);
  };

  return (
    <div className="mt-2 flex flex-col gap-0.5">
      <p className="mb-1 text-sm font-medium">{question.question}</p>
      {options.map((opt) => (
        <div key={opt.id} className="flex flex-col">
          <button
            type="button"
            onClick={() => toggleOption(opt.id)}
            onKeyDown={(event) => handleOptionKeyDown(event, opt.id)}
            className={cn(OPTION_ROW_CLASS, "text-left transition-colors hover:bg-black/5")}
          >
            <span
              data-testid="ask-user-indicator"
              className={cn(
                INDICATOR_BASE_CLASS,
                "transition-colors",
                indicatorShapeClass(question.multiSelect),
                selected[opt.id] ? "border-primary bg-primary" : "border-muted-foreground/40",
              )}
            >
              {!!selected[opt.id] && <Check className="text-primary-foreground size-3" />}
            </span>
            <span>
              {opt.label}
              {opt.description ? (
                <span className="text-muted-foreground ml-1 text-xs">— {opt.description}</span>
              ) : null}
            </span>
          </button>
          {!!selected[opt.id] && (
            <AskUserNoteField
              placeholder={
                opt.id === OTHER_OPTION_ID ? "Describe your option…" : "Add a note… (optional)"
              }
              focusOnMount={focusNoteId === opt.id}
              value={notes[opt.id] ?? ""}
              onChange={(value) => setNotes((n) => ({ ...n, [opt.id]: value }))}
            />
          )}
        </div>
      ))}
      <div className="mt-1 flex justify-end">
        <Button
          size="sm"
          variant="secondary"
          disabled={!hasSelection || isSubmitting}
          onClick={() => void handleSubmit()}
          className="h-6 px-3 text-xs"
        >
          {isSubmitting ? "Sending…" : "Submit"}
        </Button>
      </div>
    </div>
  );
}
